import React, { JSX, useContext, useEffect, useMemo, useState } from "react";

import { UserContext } from "@/app/_components/context/user-context";
import { SalesBreakdownData } from "@/app/types/types";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { DealershipContext } from "@/app/_components/context/dealership-context";

type CarouselProps = {
  loading: boolean;
  salesChart: JSX.Element;
  frontGrossChart: JSX.Element | undefined;
  backGrossChart: JSX.Element | undefined;
  salesData: SalesBreakdownData;
};

const kFormatter = (num: number) => {
  return Math.abs(num) > 999
    ? Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1)) + "K"
    : (Math.sign(num) * Math.abs(num)).toFixed(0);
};

export default function Carousel({
  loading,
  salesChart,
  frontGrossChart,
  backGrossChart,
  salesData,
}: CarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const { user, getPrimaryUserGroup } = useContext(UserContext);
  const { currentDealership } = useContext(DealershipContext);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);

  const primaryUserGroup = useMemo(() => {
    if (!currentDealership) return;
    return getPrimaryUserGroup(currentDealership.id);
  }, [currentDealership, getPrimaryUserGroup]);

  useEffect(() => {
    if (!currentDealership || !user) return;
    if (user.permissions && user.permissions[currentDealership.id]) {
      setUserPermissions(user.permissions[currentDealership.id]);
    }
    if (!primaryUserGroup) return;
    setUserPermissions(primaryUserGroup.permissions);
  }, [currentDealership, user, primaryUserGroup]);

  const items = useMemo(() => {
    if (!salesData) return [];
    return [
      {
        label: "Total Units Sold",
        chart: salesChart,
        data: {
          new: salesData["NEW"]?.units ?? 0,
          used: salesData["USED"]?.units ?? 0,
        },
      },
      ...(userPermissions.includes("VIEW_OWN_GROSS") && frontGrossChart
        ? [
            {
              label: "Total Front Gross",
              chart: frontGrossChart,
              data: {
                new: salesData["NEW"]?.frontGross ?? 0,
                used: salesData["USED"]?.frontGross ?? 0,
              },
            },
          ]
        : []),
      ...((userPermissions.includes("VIEW_OWN_BACK_GROSS") ||
        userPermissions.includes("VIEW_DASHBOARD_FULL")) &&
      backGrossChart
        ? [
            {
              label: "Total Back Gross",
              chart: backGrossChart,
              data: {
                new: salesData["NEW"]?.backGross ?? 0,
                used: salesData["USED"]?.backGross ?? 0,
              },
            },
          ]
        : []),
    ];
  }, [salesData, salesChart, frontGrossChart, backGrossChart, userPermissions]);

  const goPrevious = () =>
    setActiveIndex((i) => (i - 1 + items.length) % items.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % items.length);

  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;
    const onStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
    };
    const onMove = (e: TouchEvent) => {
      touchEndX = e.touches[0].clientX;
    };
    const onEnd = () => {
      const d = touchStartX - touchEndX;
      if (d > 50) goNext();
      else if (d < -50) goPrevious();
    };
    const el = document.querySelector("#carousel-container");
    if (el) {
      el.addEventListener("touchstart", onStart);
      el.addEventListener("touchmove", onMove);
      el.addEventListener("touchend", onEnd);
    }
    return () => {
      if (el) {
        el.removeEventListener("touchstart", onStart);
        el.removeEventListener("touchmove", onMove);
        el.removeEventListener("touchend", onEnd);
      }
    };
  }, [items]);

  if (!salesData) return null;

  return (
    <div className="w-full">
      {/* Slide strip */}
      <div className="relative w-full overflow-hidden" id="carousel-container">
        <div
          className="flex"
          style={{
            transform: `translateX(-${activeIndex * 100}%)`,
            transition: "transform 0.4s ease-in-out",
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="min-w-full flex items-center gap-3 py-2"
            >
              {/* Doughnut chart */}
              <div className="w-28 h-28 shrink-0 flex items-center justify-center">
                {loading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                ) : (
                  item.chart
                )}
              </div>

              {/* Stats */}
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <span className="text-xs font-medium text-foreground">
                  {item.label}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-[#43d662] shrink-0" />
                  <span className="text-xs text-muted-foreground flex-1">
                    New
                  </span>
                  {loading ? (
                    <div className="h-3 w-8 bg-muted animate-pulse rounded" />
                  ) : (
                    <span className="text-xs font-medium text-foreground">
                      {kFormatter(item.data.new)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-blue-400 shrink-0" />
                  <span className="text-xs text-muted-foreground flex-1">
                    Used
                  </span>
                  {loading ? (
                    <div className="h-3 w-8 bg-muted animate-pulse rounded" />
                  ) : (
                    <span className="text-xs font-medium text-foreground">
                      {kFormatter(item.data.used)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls — only shown when there are multiple items */}
      {items.length > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={goPrevious}
            disabled={activeIndex === 0}
            className="p-1 rounded-md disabled:opacity-30 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === activeIndex ? "bg-[#43d662]" : "bg-muted-foreground/30"
                }`}
              />
            ))}
          </div>

          <button
            onClick={goNext}
            disabled={activeIndex === items.length - 1}
            className="p-1 rounded-md disabled:opacity-30 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
